import { useState } from "react";
import { PiggyBank, ArrowLeftRight, Send } from "lucide-react";

const RATES = { USD: 0.0117, GBP: 0.0092, EUR: 0.0108 };
const CATS = ["Housing", "Food", "Transportation", "Savings", "Education", "Entertainment"];

export default function FinLab() {
  const [tab, setTab] = useState("budget");
  const [budget, setBudget] = useState({ Housing: 12000, Food: 8000, Transportation: 4000, Savings: 8000, Education: 5000, Entertainment: 3000 });
  const [fxAmt, setFxAmt] = useState(10000);

  const income = 40000;
  const spent = Object.values(budget).reduce((a, b) => a + (Number(b) || 0), 0);
  const savingsRate = (Number(budget.Savings) || 0) / income;
  const score = Math.max(0, Math.min(100, Math.round(50 + savingsRate * 200 - Math.max(0, (spent - income) / income) * 150)));

  const tabBtn = (id, label, icon) => (
    <button data-testid={`finlab-tab-${id}`} onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold ${tab === id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
      {icon} {label}
    </button>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Money Skills</h1>
        <div className="bg-teal-50 border border-teal-100 rounded-xl px-4 py-3 mt-2 space-y-1">
          <p className="text-sm text-teal-900 font-semibold">A practice simulator from FSCD's Financial Literacy program</p>
          <p className="text-xs text-teal-800/80 leading-relaxed">Build everyday money skills hands-on: plan a monthly household budget, see how much your family keeps as savings, and understand what happens when relatives working abroad send money home (foreign exchange &amp; remittances). Everything here is a safe simulation — <span className="font-semibold">no real money is ever involved</span>.</p>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {tabBtn("budget", "Budget Simulator", <PiggyBank size={16} />)}
        {tabBtn("fx", "Foreign Exchange", <ArrowLeftRight size={16} />)}
        {tabBtn("remit", "Remittance", <Send size={16} />)}
      </div>

      {tab === "budget" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="budget-simulator">
          <p className="text-sm text-slate-600">Monthly income: <span className="font-bold text-slate-900">₹40,000</span>. Distribute it across categories:</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {CATS.map((c) => (
              <label key={c} className="text-sm">
                <span className="text-slate-500">{c}</span>
                <input data-testid={`budget-${c.toLowerCase()}`} type="number" value={budget[c]}
                  onChange={(e) => setBudget({ ...budget, [c]: e.target.value })}
                  className="w-full h-11 px-3 mt-1 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-600" />
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <div>
              <p className={`text-sm font-semibold ${spent > income ? "text-red-600" : "text-slate-700"}`}>Allocated: ₹{spent.toLocaleString("en-IN")} / ₹{income.toLocaleString("en-IN")}</p>
              {spent > income && <p className="text-xs text-red-500">Overspending! Reduce categories.</p>}
            </div>
            <div className="text-center" data-testid="financial-health-score">
              <p className={`font-heading text-3xl font-extrabold ${score >= 70 ? "text-emerald-600" : score >= 40 ? "text-amber-600" : "text-red-600"}`}>{score}</p>
              <p className="text-xs text-slate-400">Financial Health</p>
            </div>
          </div>
        </div>
      )}

      {tab === "fx" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="fx-simulator">
          <label className="text-sm text-slate-500">Amount in INR
            <input data-testid="fx-amount-input" type="number" value={fxAmt} onChange={(e) => setFxAmt(Number(e.target.value) || 0)}
              className="w-full h-11 px-3 mt-1 rounded-xl border border-slate-300" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(RATES).map(([cur, r]) => (
              <div key={cur} className="rounded-xl border border-slate-200 p-3 text-center">
                <p className="text-xs text-slate-400">{cur}</p>
                <p className="font-heading font-extrabold text-slate-900">{(fxAmt * r * 0.98).toFixed(2)}</p>
                <p className="text-[10px] text-slate-400">after 2% fee</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">Demo rates. Banks and FinTechs add a fee + a spread on the mid-market rate — always compare the final received amount, not just the advertised rate.</p>
        </div>
      )}

      {tab === "remit" && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="remittance-simulator">
          <p className="text-sm text-slate-600">Sending <span className="font-bold">₹20,000</span> internationally (demo USD 1 = ₹85.5):</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 uppercase tracking-wider"><th className="py-2">Provider</th><th>Fee</th><th>Rate</th><th className="text-right">Recipient gets</th></tr></thead>
            <tbody>
              {[["Traditional Bank", 800, 83.2], ["FinTech App", 190, 85.1], ["Remittance Company", 350, 84.5]].map(([p, fee, rate]) => (
                <tr key={p} className="border-t border-slate-100">
                  <td className="py-2.5 font-medium text-slate-800">{p}</td>
                  <td className="text-red-600">₹{fee}</td>
                  <td>{rate}</td>
                  <td className="text-right font-bold text-slate-900">${((20000 - fee) / rate).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500">Lesson: fees AND exchange-rate spreads decide what the recipient actually receives.</p>
        </div>
      )}
    </div>
  );
}
